/**
 * 个人资料页面
 *
 * 显示和编辑用户资料，包括：
 * - 昵称（可编辑）
 * - 邮箱
 * - 等级
 * - 注册时间
 * - 订阅类型
 *
 * 使用 Cookie 认证，无需手动管理 token。
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AuthorCenterLayout } from '../components/layout/AuthorCenterLayout';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi, toUser } from '../services/authApi';
import { SliderCaptcha } from '../components/SliderCaptcha';

interface UserProfile {
  id: number;
  email: string;
  phone?: string | null;
  has_password?: boolean;
  nickname: string | null;
  level: string;
  subscription_type: string;
  is_verified: boolean;
  created_at: string;
}

const PHONE_PLACEHOLDER_SUFFIX = '@phone.starnovel.cn';

/** 手机号脱敏显示 */
const maskPhone = (phone: string): string =>
  phone.length === 11 ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : phone;

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser, isAuthenticated, initialized } = useAuthStore();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingNickname, setEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');

  // 绑定手机号相关状态
  const [showBindPhone, setShowBindPhone] = useState(false);
  const [bindPhoneValue, setBindPhoneValue] = useState('');
  const [bindCode, setBindCode] = useState('');
  const [showBindSlider, setShowBindSlider] = useState(false);
  const [bindCaptchaToken, setBindCaptchaToken] = useState<string | null>(null);
  const [codeSending, setCodeSending] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [binding, setBinding] = useState(false);

  // 设置/修改密码相关状态
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // 等待初始化完成
  useEffect(() => {
    if (initialized && !isAuthenticated) {
      navigate('/login');
    }
  }, [initialized, isAuthenticated, navigate]);

  // 加载用户资料
  const fetchProfile = useCallback(async () => {
    try {
      const data = await authApi.getProfile();
      setProfile(data);
      setNewNickname(data.nickname || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取资料失败');
      // 使用 store 中的数据作为备用
      if (user) {
        setProfile({
          id: user.id,
          email: user.email,
          phone: user.phone ?? null,
          has_password: true,
          nickname: user.nickname,
          level: user.level,
          subscription_type: user.subscription_type,
          is_verified: user.is_verified,
          created_at: user.created_at,
        });
        setNewNickname(user.nickname || '');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    }
  }, [fetchProfile, isAuthenticated]);

  // 验证昵称格式
  const validateNickname = (nickname: string): string | null => {
    if (!nickname) {
      return '昵称不能为空';
    }
    if (nickname.length < 2 || nickname.length > 20) {
      return '昵称需要2-20个字符';
    }
    // 验证字符：中文、英文、数字、下划线
    for (const char of nickname) {
      if (!(/[a-zA-Z0-9_]/.test(char) || /[\u4e00-\u9fff]/.test(char))) {
        return '昵称仅支持中文、英文、数字和下划线';
      }
    }
    return null;
  };

  // 保存昵称
  const handleSaveNickname = async () => {
    const validationError = validateNickname(newNickname);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const data = await authApi.updateProfile(newNickname);

      // 更新本地状态
      setProfile(data);
      setEditingNickname(false);

      // 更新 authStore 中的用户信息
      setUser(toUser(data));

      setSuccess('昵称已保存');
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存失败';
      // 检查是否是昵称冲突
      if (message.includes('昵称') || message.includes('已被')) {
        setError('该昵称已被使用');
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingNickname(false);
    setNewNickname(profile?.nickname || '');
    setError('');
    setSuccess('');
  };

  // 发送绑定用短信验证码（需先过滑块）
  const handleSendBindCode = useCallback(
    async (token?: string) => {
      const effectiveToken = token ?? bindCaptchaToken;
      if (!effectiveToken) {
        setShowBindSlider(true);
        return;
      }
      if (!/^1[3-9]\d{9}$/.test(bindPhoneValue)) {
        setError('请输入正确的中国大陆手机号');
        return;
      }
      setCodeSending(true);
      setError('');
      try {
        await authApi.smsSend(bindPhoneValue, effectiveToken);
        setSuccess(`验证码已发送至 ${bindPhoneValue}，请查收短信`);
        setCodeCountdown(60);
        const timer = setInterval(() => {
          setCodeCountdown((c) => {
            if (c <= 1) {
              clearInterval(timer);
              return 0;
            }
            return c - 1;
          });
        }, 1000);
      } catch (err) {
        setBindCaptchaToken(null);
        setError(err instanceof Error ? err.message : '验证码发送失败');
      } finally {
        setCodeSending(false);
      }
    },
    [bindPhoneValue, bindCaptchaToken]
  );

  // 提交绑定手机号
  const handleBindPhone = async () => {
    if (!/^1[3-9]\d{9}$/.test(bindPhoneValue)) {
      setError('请输入正确的中国大陆手机号');
      return;
    }
    if (!bindCode.trim()) {
      setError('请输入短信验证码');
      return;
    }
    setBinding(true);
    setError('');
    setSuccess('');
    try {
      const data = await authApi.bindPhone(bindPhoneValue, bindCode.trim());
      setProfile(data);
      setShowBindPhone(false);
      setBindPhoneValue('');
      setBindCode('');
      setBindCaptchaToken(null);
      setSuccess('手机号绑定成功，可使用短信验证码登录');
    } catch (err) {
      setError(err instanceof Error ? err.message : '绑定失败');
    } finally {
      setBinding(false);
    }
  };

  // 提交设置/修改密码
  const handleSetPassword = async () => {
    const hasPassword = profile?.has_password;
    if (hasPassword && !oldPassword) {
      setError('请输入当前密码');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError('新密码长度至少为8位');
      return;
    }
    if (!/^(?=.*[a-zA-Z])(?=.*\d)/.test(newPassword)) {
      setError('新密码必须包含字母和数字');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('两次输入的新密码不一致');
      return;
    }
    setSavingPassword(true);
    setError('');
    setSuccess('');
    try {
      await authApi.setPassword(newPassword, newPasswordConfirm, hasPassword ? oldPassword : undefined);
      setShowPasswordDialog(false);
      setOldPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      if (profile) {
        setProfile({ ...profile, has_password: true });
      }
      setSuccess('密码已设置，可使用邮箱/手机号+密码登录');
    } catch (err) {
      setError(err instanceof Error ? err.message : '密码设置失败');
    } finally {
      setSavingPassword(false);
    }
  };

  // 等待初始化
  if (!initialized) {
    return (
      <AuthorCenterLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
        </div>
      </AuthorCenterLayout>
    );
  }

  // 未登录
  if (!isAuthenticated) {
    return null;
  }

  // 加载中
  if (loading) {
    return (
      <AuthorCenterLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
        </div>
      </AuthorCenterLayout>
    );
  }

  return (
    <AuthorCenterLayout>
      <div className="min-h-screen bg-white">
        <div className="max-w-2xl mx-auto px-6 py-8">
          {/* 标题 */}
          <h1 className="text-2xl font-bold text-gray-900 mb-6">个人资料</h1>

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
              {error}
              <button
                onClick={() => setError('')}
                className="ml-4 underline"
              >
                关闭
              </button>
            </div>
          )}

          {/* 成功提示 */}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md text-green-700">
              {success}
            </div>
          )}

          {/* 资料卡片 */}
          {profile && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              {/* 昵称 */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      昵称
                    </label>
                    {editingNickname ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newNickname}
                          onChange={(e) => setNewNickname(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                          maxLength={20}
                          disabled={saving}
                        />
                        <button
                          onClick={handleSaveNickname}
                          disabled={saving}
                          className="px-3 py-1 bg-ink-800 text-white rounded-md hover:bg-ink-700 disabled:opacity-50"
                        >
                          {saving ? '保存中...' : '保存'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-900">
                          {profile.nickname || '未设置'}
                        </span>
                        <button
                          onClick={() => setEditingNickname(true)}
                          className="text-paper-600 hover:text-paper-700 text-sm"
                        >
                          编辑
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 手机号 */}
              <div className="px-6 py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  手机号
                </label>
                <div className="flex items-center gap-2">
                  {profile.phone ? (
                    <>
                      <span className="text-gray-900">{maskPhone(profile.phone)}</span>
                      <span className="text-green-600 text-sm">已验证</span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-400">未绑定</span>
                      <button
                        onClick={() => {
                          setShowBindPhone(true);
                          setError('');
                          setSuccess('');
                        }}
                        className="text-paper-600 hover:text-paper-700 text-sm"
                      >
                        绑定手机号
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* 邮箱 */}
              <div className="px-6 py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  邮箱
                </label>
                {profile.email.endsWith(PHONE_PLACEHOLDER_SUFFIX) ? (
                  <span className="text-gray-400">未绑定</span>
                ) : (
                  <>
                    <span className="text-gray-900">{profile.email}</span>
                    {profile.is_verified ? (
                      <span className="ml-2 text-green-600 text-sm">已验证</span>
                    ) : (
                      <span className="ml-2 text-yellow-600 text-sm">未验证</span>
                    )}
                  </>
                )}
              </div>

              {/* 密码 */}
              <div className="px-6 py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  密码
                </label>
                <div className="flex items-center gap-2">
                  {profile.has_password ? (
                    <>
                      <span className="text-gray-900">已设置</span>
                      <button
                        onClick={() => {
                          setShowPasswordDialog(true);
                          setError('');
                          setSuccess('');
                        }}
                        className="text-paper-600 hover:text-paper-700 text-sm"
                      >
                        修改密码
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-400">未设置（当前仅短信登录）</span>
                      <button
                        onClick={() => {
                          setShowPasswordDialog(true);
                          setError('');
                          setSuccess('');
                        }}
                        className="text-paper-600 hover:text-paper-700 text-sm"
                      >
                        设置密码
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* 等级 */}
              <div className="px-6 py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  等级
                </label>
                <span className="text-gray-900">{profile.level}</span>
              </div>

              {/* 订阅类型 */}
              <div className="px-6 py-4 border-b border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  订阅类型
                </label>
                <span className="text-gray-900 capitalize">
                  {profile.subscription_type === 'premium' ? '高级会员' : '免费会员'}
                </span>
              </div>

              {/* 注册时间 */}
              <div className="px-6 py-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  注册时间
                </label>
                <span className="text-gray-900">
                  {new Date(profile.created_at).toLocaleDateString('zh-CN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            </div>
          )}

          {/* 绑定手机号对话框 */}
          {showBindPhone && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-bold text-gray-900 mb-4">绑定手机号</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">手机号</label>
                    <input
                      type="tel"
                      value={bindPhoneValue}
                      onChange={(e) => setBindPhoneValue(e.target.value.trim())}
                      placeholder="请输入中国大陆手机号"
                      maxLength={11}
                      disabled={binding}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">验证码</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={bindCode}
                        onChange={(e) => setBindCode(e.target.value.trim())}
                        placeholder="短信验证码"
                        maxLength={6}
                        disabled={binding}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                      />
                      <button
                        onClick={() => handleSendBindCode()}
                        disabled={codeSending || codeCountdown > 0}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 whitespace-nowrap hover:bg-gray-50"
                      >
                        {codeCountdown > 0
                          ? `${codeCountdown}s`
                          : codeSending
                            ? '发送中...'
                            : '获取验证码'}
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">绑定后可使用短信验证码登录，且需通过滑块验证后才能获取验证码。</p>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => {
                        setShowBindPhone(false);
                        setError('');
                      }}
                      disabled={binding}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleBindPhone}
                      disabled={binding}
                      className="px-4 py-2 bg-ink-800 text-white rounded-md hover:bg-ink-700 disabled:opacity-50"
                    >
                      {binding ? '绑定中...' : '确认绑定'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 滑块验证弹层（绑定手机号发送验证码前） */}
          {showBindPhone && showBindSlider && (
            <SliderCaptcha
              onSuccess={(token) => {
                setBindCaptchaToken(token);
                setShowBindSlider(false);
                handleSendBindCode(token);
              }}
              onClose={() => setShowBindSlider(false)}
            />
          )}

          {/* 设置/修改密码对话框 */}
          {showPasswordDialog && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-bold text-gray-900 mb-4">
                  {profile?.has_password ? '修改密码' : '设置密码'}
                </h2>
                <div className="space-y-4">
                  {profile?.has_password && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">当前密码</label>
                      <input
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="请输入当前密码"
                        disabled={savingPassword}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="至少8位，包含字母和数字"
                      disabled={savingPassword}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                    <input
                      type="password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      placeholder="再次输入新密码"
                      disabled={savingPassword}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => {
                        setShowPasswordDialog(false);
                        setError('');
                      }}
                      disabled={savingPassword}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSetPassword}
                      disabled={savingPassword}
                      className="px-4 py-2 bg-ink-800 text-white rounded-md hover:bg-ink-700 disabled:opacity-50"
                    >
                      {savingPassword ? '保存中...' : '保存'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthorCenterLayout>
  );
};

export default ProfilePage;